import React, { useState, useEffect } from 'react';

import Select from '~/components/Select';
import DistributedColumnsPrices1 from '~/components/StatisticsCharts/DistributedColumnsPrices1';
import DistributedColumnsPrices2 from '~/components/StatisticsCharts/DistributedColumnsPrices2';
import DistributedColumnsPrices3 from '~/components/StatisticsCharts/DistributedColumnsPrices3';

import { getRepValueForSelect, getClientValueForSelect } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { STATE_OPTIONS } from '../../../constants';

interface IFilters {
  state: string;
  state_refreshed: boolean;
  erp_rep_code: string;
  rep_refreshed: boolean;
  erp_client_code: string;
  status: string;
  year: string;
  refresh: boolean;
  refresh_projects: boolean;
}

const Prices: React.FC = () => {
  const { user } = useAuth();
  const [representatives, setRepresentatives] = useState<
    {
      code: string;
      name: string;
    }[]
  >();
  const [clients, setClients] = useState<
    {
      code: string;
      company_name: string;
    }[]
  >();
  const [projects, setProjects] = useState<
    {
      type: string;
      price?: number;
      shipping_price?: number;
      total_price?: number;
      gross_discount_price?: number;
      net_discount_price?: number;
      total_tax_deductions_price?: number;
      total_event_costs_price?: number;
    }[]
  >([]);
  const [filters, setFilters] = useState<IFilters>({
    state: 'ALL',
    state_refreshed: true,
    erp_rep_code: user.user_type.type === 'REP' ? user.erp_rep_code : 'ALL',
    rep_refreshed: true,
    erp_client_code: 'ALL',
    status: 'ALL',
    year: 'ALL',
    refresh: true,
    refresh_projects: false,
  });
  const [displayProjects, setDisplayProjects] = useState({
    price: 0,
    shipping_price: 0,
    total_price: 0,
  });
  const [materialProjects, setMaterialProjects] = useState({
    price: 0,
    shipping_price: 0,
    total_price: 0,
  });
  const [sponsorshipProjects, setSponsorshipProjects] = useState({
    gross_discount: 0,
    total_tax_deductions: 0,
    net_discount: 0,
  });
  const [sampleProjects, setSampleProjects] = useState({
    price: 0,
  });
  const [eventProjects, setEventProjects] = useState({
    total_event_costs: 0,
  });

  useEffect(() => {
    const refreshFilters = async () => {
      let { erp_rep_code, erp_client_code, rep_refreshed } = filters;

      if (filters.state_refreshed) {
        await api
          .get(`/localrepresentatives/state?state=${filters.state}`)
          .then(response => {
            setRepresentatives([
              {
                code: 'ALL',
                name: '',
              },
              ...response.data,
            ]);

            erp_rep_code = 'ALL';
            rep_refreshed = true;
          });
      }

      if (rep_refreshed) {
        erp_rep_code =
          user.user_type.type === 'REP' ? user.erp_rep_code : erp_rep_code;

        await api
          .get(`/localclients/rep?rep_code=${erp_rep_code}`)
          .then(response => {
            setClients([
              {
                code: 'ALL',
                company_name: '',
              },
              ...response.data,
            ]);

            erp_client_code = 'ALL';
          });
      }

      setFilters({
        ...filters,
        state_refreshed: false,
        erp_rep_code,
        rep_refreshed: false,
        erp_client_code,
        refresh: false,
        refresh_projects: true,
      });
    };

    if (filters.refresh) {
      refreshFilters();
    }
  }, [filters, user]);

  useEffect(() => {
    const loadProjects = async () => {
      await api
        .get(
          `/projects/projectstatistics?` +
            `state=${filters.state}&` +
            `erp_rep_code=${filters.erp_rep_code}&` +
            `erp_client_code=${filters.erp_client_code}&` +
            `status=${filters.status}&` +
            `year=${filters.year}`,
        )
        .then(response => {
          setProjects(response.data);

          setFilters({ ...filters, refresh_projects: false });
        });
    };

    if (filters.refresh_projects) {
      loadProjects();
    }
  }, [filters]);

  useEffect(() => {
    if (projects.length > 0) {
      setDisplayProjects(
        projects
          .filter(p => p.type === 'DISPLAY')
          .reduce(
            (acc, curr) => {
              return {
                price: acc.price + (curr.price || 0),
                shipping_price: acc.shipping_price + (curr.shipping_price || 0),
                total_price: acc.total_price + (curr.total_price || 0),
              };
            },
            {
              price: 0,
              shipping_price: 0,
              total_price: 0,
            },
          ),
      );

      setMaterialProjects(
        projects
          .filter(p => p.type === 'MATERIAL')
          .reduce(
            (acc, curr) => {
              return {
                price: acc.price + (curr.price || 0),
                shipping_price: acc.shipping_price + (curr.shipping_price || 0),
                total_price: acc.total_price + (curr.total_price || 0),
              };
            },
            {
              price: 0,
              shipping_price: 0,
              total_price: 0,
            },
          ),
      );

      setSponsorshipProjects(
        projects
          .filter(p => p.type === 'PATROCINIO')
          .reduce(
            (acc, curr) => {
              return {
                gross_discount:
                  acc.gross_discount + (curr.gross_discount_price || 0),
                total_tax_deductions:
                  acc.total_tax_deductions +
                  (curr.total_tax_deductions_price || 0),
                net_discount: acc.net_discount + (curr.net_discount_price || 0),
              };
            },
            {
              gross_discount: 0,
              total_tax_deductions: 0,
              net_discount: 0,
            },
          ),
      );

      setSampleProjects(
        projects
          .filter(p => p.type === 'AMOSTRA')
          .reduce(
            (acc, curr) => {
              return {
                price: acc.price + (curr.price || 0),
              };
            },
            {
              price: 0,
            },
          ),
      );

      setEventProjects(
        projects
          .filter(p => p.type === 'EVENTO')
          .reduce(
            (acc, curr) => {
              return {
                total_event_costs:
                  acc.total_event_costs + (curr.total_event_costs_price || 0),
              };
            },
            {
              total_event_costs: 0,
            },
          ),
      );
    }
  }, [projects]);

  const handleFilter = (): React.ReactNode => {
    if (user.user_type.type !== 'REP') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ width: 250 }}>
            <Select
              label="Estado"
              options={STATE_OPTIONS}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  state: e.value,
                  state_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Representante"
              options={representatives?.map(representative => ({
                value: representative.code,
                label:
                  representative.code === 'ALL'
                    ? 'Todos'
                    : `(${representative.code}) - ${representative.name}`,
              }))}
              value={getRepValueForSelect(
                filters.erp_rep_code,
                representatives,
              )}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_rep_code: e.value,
                  rep_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Cliente"
              options={clients?.map(client => ({
                value: client.code,
                label:
                  client.code === 'ALL'
                    ? 'Todos'
                    : `(${client.code}) - ${client.company_name}`,
              }))}
              value={getClientValueForSelect(filters.erp_client_code, clients)}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_client_code: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Status"
              options={[
                { value: 'Pendente', label: 'Pendente' },
                { value: 'Finalizado', label: 'Finalizado' },
                { value: 'Cancelado', label: 'Cancelado' },
                { value: 'ALL', label: 'Todos' },
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  status: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Ano"
              options={[
                { value: 'ALL', label: 'Todos' },
                ...Array.from(
                  { length: new Date().getFullYear() - 2021 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map(y => ({ value: String(y), label: String(y) })),
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  year: e.value,
                  refresh: true,
                });
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      {handleFilter()}

      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}
      >
        <DistributedColumnsPrices1
          label="Displays"
          price={displayProjects.price}
          shippingPrice={displayProjects.shipping_price}
          totalPrice={displayProjects.total_price}
        />

        <DistributedColumnsPrices1
          label="Materiais"
          price={materialProjects.price}
          shippingPrice={materialProjects.shipping_price}
          totalPrice={materialProjects.total_price}
        />

        <DistributedColumnsPrices2
          label="Pagamentos"
          grossDiscountPrice={sponsorshipProjects.gross_discount}
          totalTaxDeductionsPrice={sponsorshipProjects.total_tax_deductions}
          netDiscountPrice={sponsorshipProjects.net_discount}
        />

        <DistributedColumnsPrices3
          label="Amostras"
          price={sampleProjects.price}
        />

        <DistributedColumnsPrices3
          label="Eventos"
          category="Valor Custos"
          price={eventProjects.total_event_costs}
        />
      </div>
    </div>
  );
};

export default Prices;
